import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const ArticlePage = () => {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = getArticleById(articleId);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.id, article.category);

  const articleSchema = {
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    url: `https://techcommunity.cn/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '草榴社区技术讨论区',
    },
  };

  const webPageSchema = {
    '@type': 'WebPage',
    name: `${article.title} - 草榴社区技术讨论区`,
    description: article.excerpt,
    url: `https://techcommunity.cn/article/${article.id}`,
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={webPageSchema} />
      <SchemaOrg type="Article" data={articleSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category?.name || article.category, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {category?.name || article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                    title={article.author}
                  />
                  <div>
                    <div className="font-medium text-foreground-800">{article.author}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views} 阅读
                  </span>
                </div>
              </div>
            </header>

            <div
              className="article-content pb-8 border-b border-background-200/70"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center justify-between py-6 border-b border-background-200/70">
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-200/70 text-sm text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors whitespace-nowrap">
                  <i className="ri-heart-line"></i> 点赞 ({article.likes})
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-200/70 text-sm text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-200/70 text-sm text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors whitespace-nowrap">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-500">
                <i className="ri-chat-3-line"></i> {article.comments} 条评论
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-8">
                <h3 className="text-lg font-semibold text-foreground-950 mb-4">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="block bg-background-100 rounded-lg p-4 hover:bg-background-200/70 transition-colors group"
                    >
                      <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{related.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default ArticlePage;